<template>
  <div>
    <tf-table
      v-if="isLoaded"
      v-bind="{
        filterValues: $route.query.filters,
        findIndexBy: 'product_id',
        parentalControls: true,
        pageLimit: 100,
        tableData,
        tableMeta: {
          columns,
          sort: {
            direction: $route.query.sortDirection || 'asc',
            prop: $route.query.sortProp || 'product_type_name',
          }
        },
      }"
      @changeFilter="updateQueryParams('filters', $event)"
      @changeSortCriteria="changeSortCriteria"
    >
      <template
        v-if="error"
        #error
      >
        {{ error }}
      </template>
      <tf-table-parent-row
        :key="rowData.id"
        slot="parentRow"
        slot-scope="{ rowData }"
      >
        <tf-table-td>
          <tf-button
            type="primary"
            size="text"
            @click="togglePanel(rowData)"
            v-text="rowData.product_type_name"
          />
        </tf-table-td>
        <tf-table-td>
          {{ rowData.carrier_name }}
        </tf-table-td>
        <commissions-cell :commissions="rowData.commissions" />
        <tf-table-td>
          {{ rowData.effectiveDates || '&mdash;' }}
        </tf-table-td>
      </tf-table-parent-row>
    </tf-table>
    <tf-panel2
      v-if="activeCoverage.product_id"
      v-test:panel="'prior coverage'"
      open
      max-width="700px"
      top="53px"
      :closable="false"
    >
      <header>
        <h2>{{ activeCoverage.product_type_name }} (Prior Coverage)</h2>
        <h3>Coverage effective dates: {{ activeCoverage.effectiveDates || '&mdash;' }}</h3>
        <div>
          <img
            v-bind="{
              src: selectedCarrier.logo_url,
              alt: selectedCarrier.name,
            }"
            height="63"
          >
          <cta-download-coverage
            v-bind="{
              coverageType: 'past',
              clientId,
              productTypeId: activeCoverage.product_type_id,
            }"
          />
        </div>
        <tf-button
          id="btn-close-panel"
          type="primary"
          icon="times"
          size="icon-no-fill"
          no-border
          no-branding
          @click="togglePanel"
        />
      </header>
      <table-details :product-id="activeCoverage.product_id" />
    </tf-panel2>
    <router-view />
  </div>
</template>

<script>
  import { useCarriers } from '@/stores/carriers';
  import { mapState } from 'pinia';
  import RouterMixin from '@/mixins/router';
  import { getCoveragesV2 } from '@/services/clientCoverages';

  /**
   * The "Coverage History" is a tabbed view within the "Client History" page that is used to display relevant data to the broker.
   *
   * @vuedoc
   * @exports CoverageHistory
   * @category Views
   */
  export default {
    name: 'CoverageHistory',
    components: {
      CtaDownloadCoverage: () => import('@/components/Ctas/DownloadCoverage'),
      CommissionsCell: () => import('@/components/Employer/Tables/CommissionsCell'),
      TableDetails: () => import('@/components/Coverage/Tables/Details'),
    },
    mixins: [RouterMixin],
    props: {
      /**
       * The ID of the employer/client used to interact with the API.
       */
      clientId: {
        type: [String, Number],
        required: true,
      },
    },
    data: () => ({
      activeCoverage: {},
      isDownloadingCoverage: false,
      columns: [
        {
          filter: {
            type: 'string',
            width: '200px',
          },
          label: 'Product(s)',
          prop: 'product_type_name',
          sortable: true,
        },
        {
          filter: {
            type: 'string',
            width: '200px',
          },
          label: 'Carrier',
          prop: 'carrier_name',
          sortable: true,
        },
        {
          label: 'Commission',
          prop: 'commissions',
        },
        {
          label: 'Coverage effective dates',
          prop: 'effective_date',
        },
      ],
      error: null,
      isLoaded: false,
      tableData: [],
    }),
    computed: {
      ...mapState(useCarriers, ['carriers']),
      /**
       * Evaluate the selected product's carrier ID to return the appropriate carrier for use in the template as a logo.
       *
       * @returns {object}
       */
      selectedCarrier() {
        return this.activeCoverage.carrier_id
          ? this.carriers[this.activeCoverage.carrier_id]
          : {};
      },
    },
    created() {
      this.loadCoverageHistory();
    },
    methods: {
      /**
       * Load the coverage history and format the commissions to pass to the table component.
       */
      async loadCoverageHistory() {
        try {
          const { past_coverages: pastCoverages } = await getCoveragesV2({
            clientId: this.clientId,
            coverageType: 'past',
          });

          this.$set(
            this,
            'tableData',
            pastCoverages.map((coverage) => {
              const startDate = this.$TF.DatesUtils.formatSimpleDate(coverage.effective_date, 'MM/DD/YYYY');
              const endDate = this.$TF.DatesUtils.formatSimpleDate(coverage.renewal_date, 'MM/DD/YYYY');
              let effectiveDates;

              if (!startDate && endDate) {
                effectiveDates = `Ended on ${endDate}`;
              } else if (startDate && !endDate) {
                effectiveDates = `Started on ${startDate}`;
              } else if (startDate && endDate) {
                effectiveDates = `${startDate} - ${endDate}`;
              }

              return { ...coverage, effectiveDates };
            }),
          );
        } catch (error) {
          this.error = 'There was an error getting this client’s coverage history.';
        } finally {
          this.isLoaded = true;
        }
      },
      /**
       * Set the 'activeCoverage' object correctly to determine to show the panel or not.
       *
       * @param {object} coverageInfo
       */
      togglePanel(coverageInfo) {
        this.$set(
          this,
          'activeCoverage',
          coverageInfo.product_type_id ? coverageInfo : {},
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  ::v-deep .panel-content {
    overflow-y: hidden;

    > section {
      padding: 30px 30px 10px;
      text-align: left;
      overflow-y: auto;
    }

    > header {
      padding: 15px 30px 15px 40px;
    }
  }

  header {
    flex-direction: column;

    > div {
      display: flex;
      width: 100%;
      flex-direction: row;
      align-items: baseline;
      justify-content: space-between;
    }

    .tf-button {
      display: inline-flex;
    }

    h2 span,
    h3 {
      font-weight: 400;
    }

    h2 {
      margin: 2px 0 5px;
    }

    h3 {
      padding: 0 0 5px;
    }
  }

  #btn-close-panel {
    position: absolute;
    top: 10px;
    right: 15px;
    color: $tf-dark-gray;
  }
</style>
